import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
    useGetBlockedUsers as useGetBlockedUsersApi,
    useGetLoginAttempts as useGetLoginAttemptsApi,
    useBlockUser as useBlockUserApi,
    useUnblockUser as useUnblockUserApi,
    useResetFailedAttempts as useResetFailedAttemptsApi,
    useGetSecurityConfig as useGetSecurityConfigApi,
    useAutocompleteUsernames as useAutocompleteUsernamesApi,
    useGetLoginActivitySummary as useGetLoginActivitySummaryApi,
    useGetActiveSessions as useGetActiveSessionsApi,
} from '@/shared/api/client/user-security';

/**
 * Hook to fetch all blocked users
 */
export const useGetBlockedUsers = () => {
    return useGetBlockedUsersApi();
};

/**
 * Hook to fetch login attempts for a specific user with optional date filtering
 */
export const useGetLoginAttempts = (
    username: string, 
    days: number = 30, 
    startDate?: string,
    endDate?: string,
    options?: { enabled?: boolean }
) => {
    return useGetLoginAttemptsApi(
        username, 
        { days, startDate, endDate }, 
        {
            query: {
                enabled: options?.enabled !== undefined ? options.enabled : !!username,
            }
        }
    );
};

/**
 * Hook to block a user
 */
export const useBlockUser = () => {
    const queryClient = useQueryClient();
    
    return useBlockUserApi({
        mutation: {
            onSuccess: () => {
                // Invalidate blocked users query
                queryClient.invalidateQueries({ queryKey: ['/api/admin/user-security/blocked-users'] });
            },
        },
    });
};

/**
 * Hook to unblock a user
 */
export const useUnblockUser = () => {
    const queryClient = useQueryClient();
    
    return useUnblockUserApi({
        mutation: {
            onSuccess: () => {
                // Invalidate blocked users query
                queryClient.invalidateQueries({ queryKey: ['/api/admin/user-security/blocked-users'] });
            },
        },
    });
};

/**
 * Hook to reset failed attempts for a user
 */
export const useResetFailedAttempts = () => {
    const queryClient = useQueryClient();
    
    return useResetFailedAttemptsApi({
        mutation: {
            onSuccess: () => {
                // Invalidate blocked users query
                queryClient.invalidateQueries({ queryKey: ['/api/admin/user-security/blocked-users'] });
            },
        },
    });
};

/**
 * Hook to get security configuration
 */
export const useGetSecurityConfig = () => {
    return useGetSecurityConfigApi();
};

/**
 * Hook to autocomplete usernames
 */
export const useAutocompleteUsernames = (query: string, limit: number = 10, options?: { enabled?: boolean }) => {
    return useAutocompleteUsernamesApi(
        { query, limit },
        {
            query: {
                enabled: options?.enabled !== undefined ? options.enabled : query.length >= 2,
            }
        }
    );
};

/**
 * Hook to get login activity summary
 */
export const useGetLoginActivitySummary = (username?: string, days: number = 30, options?: { enabled?: boolean }) => {
    return useGetLoginActivitySummaryApi(
        { username, days },
        {
            query: {
                enabled: options?.enabled !== undefined ? options.enabled : true,
            }
        }
    );
};

/**
 * Hook to get active sessions
 */
export const useGetActiveSessions = () => {
    return useGetActiveSessionsApi();
};

/**
 * Hook to get detailed login analytics for graphs and reports
 */
export const useGetLoginAnalytics = (days: number = 30, startDate?: string, endDate?: string, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: ['/api/admin/user-security/analytics', days, startDate, endDate],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('days', days.toString());
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            
            const response = await fetch(`/api/admin/user-security/analytics?${params.toString()}`);
            if (!response.ok) {
                throw new Error('Failed to fetch login analytics');
            }
            return response.json();
        },
        enabled: options?.enabled !== undefined ? options.enabled : true,
    });
};
